// src/scene/LightingRig.tsx
/**
 * Cinematic Lighting Rig
 *
 * Studio-style 3-light setup with HDRI environment.
 * Produces soft highlights, controlled contrast, no blown whites.
 * Designed for product-render aesthetic.
 */

import { useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Environment, Lightformer } from '@react-three/drei'
import { useControls, folder } from 'leva'
import { Group, DirectionalLight, SpotLight } from 'three'
import { palette } from './tokens'

// ============================================================================
// LIGHTING RIG COMPONENT
// ============================================================================

interface LightingRigProps {
  /** Intensity multiplier for all lights */
  intensity?: number
  /** Enable shadow casting */
  shadows?: boolean
  /** HDRI environment preset or path */
  environment?: 'city' | 'sunset' | 'dawn' | 'night' | 'warehouse' | 'forest' | 'apartment' | 'studio' | 'park' | 'lobby'
  /** Environment map intensity */
  envIntensity?: number
}

export function LightingRig({
  intensity = 1,
  shadows = true,
  environment = 'city',
  envIntensity = 0.4,
}: LightingRigProps) {
  const keyLightRef = useRef<DirectionalLight>(null)
  const fillLightRef = useRef<DirectionalLight>(null)
  const rimLightRef = useRef<SpotLight>(null)

  return (
    <>
      {/* ================================================================== */}
      {/* HDRI ENVIRONMENT                                                   */}
      {/* Provides ambient lighting and realistic reflections                */}
      {/* ================================================================== */}
      <Environment
        preset={environment}
        background={false}
        environmentIntensity={envIntensity}
      >
        {/* Custom lightformers for additional control */}
        <Lightformer
          intensity={0.5 * intensity}
          rotation-x={Math.PI / 2}
          position={[0, 4, -6]}
          scale={[10, 1, 1]}
          color="#c0d0ff"
        />
        <Lightformer
          intensity={0.3 * intensity}
          rotation-y={Math.PI / 2}
          position={[-5, 2, 0]}
          scale={[20, 0.5, 1]}
          color="#ffd0c0"
        />
      </Environment>

      {/* ================================================================== */}
      {/* KEY LIGHT (Main light source)                                      */}
      {/* Positioned high and to the right, warm-neutral color               */}
      {/* Primary shadow caster                                              */}
      {/* ================================================================== */}
      <directionalLight
        ref={keyLightRef}
        position={[5, 6, 4]}
        intensity={1.0 * intensity}
        color="#fff8f0"
        castShadow={shadows}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-far={30}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
        shadow-bias={-0.0001}
        shadow-normalBias={0.02}
        shadow-radius={3}
      />

      {/* ================================================================== */}
      {/* FILL LIGHT (Softens shadows)                                       */}
      {/* Positioned opposite key light, cooler color                        */}
      {/* No shadow casting                                                  */}
      {/* ================================================================== */}
      <directionalLight
        ref={fillLightRef}
        position={[-4, 3, -2]}
        intensity={0.35 * intensity}
        color="#d0e0ff"
      />

      {/* ================================================================== */}
      {/* RIM LIGHT (Edge separation)                                        */}
      {/* Positioned behind subject, creates edge highlight                  */}
      {/* Warm accent color                                                  */}
      {/* ================================================================== */}
      <spotLight
        ref={rimLightRef}
        position={[0, 4, -6]}
        angle={0.5}
        penumbra={0.8}
        intensity={0.6 * intensity}
        color="#ffe0c0"
        distance={15}
        decay={2}
      />

      {/* ================================================================== */}
      {/* AMBIENT FILL (Very subtle)                                         */}
      {/* Prevents pure black shadows                                        */}
      {/* ================================================================== */}
      <ambientLight
        intensity={0.08 * intensity}
        color="#8090b0"
      />

      {/* ================================================================== */}
      {/* ACCENT LIGHT (Optional branded color)                              */}
      {/* Very subtle, adds depth to the scene                               */}
      {/* ================================================================== */}
      <pointLight
        position={[-2, 1, 3]}
        intensity={0.15 * intensity}
        color={palette.primary[500]}
        distance={8}
        decay={2}
      />
    </>
  )
}

// ============================================================================
// ANIMATED LIGHTING (follows camera/stage)
// ============================================================================

interface AnimatedLightingProps {
  intensity?: number
  shadows?: boolean
}

export function AnimatedLighting({
  intensity = 1,
  shadows = true,
}: AnimatedLightingProps) {
  const groupRef = useRef<Group>(null)
  const { camera } = useThree()

  // Subtle light animation - lights follow camera slightly
  useFrame(() => {
    if (!groupRef.current) return

    // Smooth follow camera X position (very subtle)
    const targetX = camera.position.x * 0.1
    groupRef.current.position.x += (targetX - groupRef.current.position.x) * 0.02
  })

  return (
    <group ref={groupRef}>
      <LightingRig intensity={intensity} shadows={shadows} />
    </group>
  )
}

// ============================================================================
// MINIMAL LIGHTING (for performance-critical scenarios)
// ============================================================================

export function MinimalLighting({ intensity = 1 }: { intensity?: number }) {
  return (
    <>
      <Environment preset="city" background={false} environmentIntensity={0.3} />
      <directionalLight
        position={[5, 5, 5]}
        intensity={0.8 * intensity}
        color="#fff8f0"
        castShadow
        shadow-mapSize={1024}
      />
      <ambientLight intensity={0.15 * intensity} color="#8090b0" />
    </>
  )
}

// ============================================================================
// STUDIO LIGHTING (maximum quality for hero shots)
// ============================================================================

// Default face position for avatar (should match avatar head position)
const DEFAULT_FACE_CENTER: [number, number, number] = [0, 1.5, 1.5]

// Light positions RELATIVE to face center (portrait lighting setup)
const LIGHT_OFFSETS = {
  key: [1.2, 0.8, 1.8] as [number, number, number],
  fill: [-1.0, 0.3, 1.5] as [number, number, number],
  face: [0, 0.1, 2.5] as [number, number, number],
  rim: [-0.3, 1.2, -1.0] as [number, number, number],
}

// Tuned intensities for portrait lighting
const DEFAULT_INTENSITIES = {
  key: 0.6,
  fill: 1.0,
  face: 0.9,
  rim: 1.5,
  ambient: 0.15,
  env: 0.01,
}

export function StudioLighting({ intensity = 1 }: { intensity?: number }) {
  // Leva controls for lighting
  const keyLightRef = useRef<DirectionalLight>(null)
  const fillLightRef = useRef<DirectionalLight>(null)
  const faceLightRef = useRef<DirectionalLight>(null)
  const rimLightRef = useRef<SpotLight>(null)

  const {
    showHelpers,
    faceCenterX,
    faceCenterY,
    faceCenterZ,
    keyIntensity,
    fillIntensity,
    faceIntensity,
    rimIntensity,
    ambientIntensity,
    envIntensity,
    keyOffsetX,
    keyOffsetY,
    keyOffsetZ,
    fillOffsetX,
    fillOffsetY,
    fillOffsetZ,
    faceOffsetX,
    faceOffsetY,
    faceOffsetZ,
    rimOffsetX,
    rimOffsetY,
    rimOffsetZ,
    keyColor,
    fillColor,
    faceColor,
    rimColor,
    ambientColor,
  } = useControls('Lighting', {
    showHelpers: { value: false, label: 'Show Light Helpers' },
    'Face Center': folder({
      faceCenterX: { value: DEFAULT_FACE_CENTER[0], min: -3, max: 3, step: 0.01 },
      faceCenterY: { value: DEFAULT_FACE_CENTER[1], min: 0, max: 3, step: 0.01 },
      faceCenterZ: { value: DEFAULT_FACE_CENTER[2], min: -2, max: 5, step: 0.01 },
    }),
    Intensities: folder({
      keyIntensity: { value: DEFAULT_INTENSITIES.key, min: 0, max: 3, step: 0.01 },
      fillIntensity: { value: DEFAULT_INTENSITIES.fill, min: 0, max: 3, step: 0.01 },
      faceIntensity: { value: DEFAULT_INTENSITIES.face, min: 0, max: 3, step: 0.01 },
      rimIntensity: { value: DEFAULT_INTENSITIES.rim, min: 0, max: 5, step: 0.01 },
      ambientIntensity: { value: DEFAULT_INTENSITIES.ambient, min: 0, max: 1, step: 0.01 },
      envIntensity: { value: DEFAULT_INTENSITIES.env, min: 0, max: 1, step: 0.01 },
    }),
    'Key Light': folder({
      keyOffsetX: { value: LIGHT_OFFSETS.key[0], min: -5, max: 5, step: 0.01 },
      keyOffsetY: { value: LIGHT_OFFSETS.key[1], min: -5, max: 5, step: 0.01 },
      keyOffsetZ: { value: LIGHT_OFFSETS.key[2], min: -5, max: 5, step: 0.01 },
      keyColor: { value: '#fffaf5' },
    }, { collapsed: true }),
    'Fill Light': folder({
      fillOffsetX: { value: LIGHT_OFFSETS.fill[0], min: -5, max: 5, step: 0.01 },
      fillOffsetY: { value: LIGHT_OFFSETS.fill[1], min: -5, max: 5, step: 0.01 },
      fillOffsetZ: { value: LIGHT_OFFSETS.fill[2], min: -5, max: 5, step: 0.01 },
      fillColor: { value: '#e5f0ff' },
    }, { collapsed: true }),
    'Face Fill': folder({
      faceOffsetX: { value: LIGHT_OFFSETS.face[0], min: -5, max: 5, step: 0.01 },
      faceOffsetY: { value: LIGHT_OFFSETS.face[1], min: -5, max: 5, step: 0.01 },
      faceOffsetZ: { value: LIGHT_OFFSETS.face[2], min: -5, max: 5, step: 0.01 },
      faceColor: { value: '#fff8ff' },
    }, { collapsed: true }),
    'Rim Light': folder({
      rimOffsetX: { value: LIGHT_OFFSETS.rim[0], min: -5, max: 5, step: 0.01 },
      rimOffsetY: { value: LIGHT_OFFSETS.rim[1], min: -5, max: 5, step: 0.01 },
      rimOffsetZ: { value: LIGHT_OFFSETS.rim[2], min: -5, max: 5, step: 0.01 },
      rimColor: { value: '#ffe8d0' },
    }, { collapsed: true }),
    'Ambient': folder({
      ambientColor: { value: '#8090b0' },
    }, { collapsed: true }),
  }, { collapsed: true })

  // Calculate absolute positions from face center + offsets
  const fc: [number, number, number] = [faceCenterX, faceCenterY, faceCenterZ]
  const keyPos: [number, number, number] = [fc[0] + keyOffsetX, fc[1] + keyOffsetY, fc[2] + keyOffsetZ]
  const fillPos: [number, number, number] = [fc[0] + fillOffsetX, fc[1] + fillOffsetY, fc[2] + fillOffsetZ]
  const facePos: [number, number, number] = [fc[0] + faceOffsetX, fc[1] + faceOffsetY, fc[2] + faceOffsetZ]
  const rimPos: [number, number, number] = [fc[0] + rimOffsetX, fc[1] + rimOffsetY, fc[2] + rimOffsetZ]

  return (
    <>
      {/* HDRI Environment */}
      <Environment preset="studio" background={false} environmentIntensity={envIntensity}>
        <Lightformer
          form="rect"
          intensity={1.0 * intensity}
          position={[0, 4, 2]}
          rotation={[Math.PI / 2.5, 0, 0]}
          scale={[6, 6, 1]}
          color="#ffffff"
        />
        <Lightformer
          form="rect"
          intensity={0.4 * intensity}
          position={[-3, 1.5, 2]}
          rotation={[0, Math.PI / 3, 0]}
          scale={[4, 3, 1]}
          color="#e8f0ff"
        />
        <Lightformer
          form="rect"
          intensity={0.3 * intensity}
          position={[3, 1.5, 2]}
          rotation={[0, -Math.PI / 3, 0]}
          scale={[4, 3, 1]}
          color="#fff0e8"
        />
      </Environment>

      {/* KEY LIGHT */}
      <directionalLight
        ref={keyLightRef}
        position={keyPos}
        intensity={keyIntensity * intensity}
        color={keyColor}
        castShadow
        shadow-mapSize={2048}
        shadow-camera-far={20}
        shadow-camera-left={-4}
        shadow-camera-right={4}
        shadow-camera-top={4}
        shadow-camera-bottom={-4}
        shadow-bias={-0.0001}
        shadow-normalBias={0.02}
      />

      {/* FILL LIGHT */}
      <directionalLight
        ref={fillLightRef}
        position={fillPos}
        intensity={fillIntensity * intensity}
        color={fillColor}
      />

      {/* FACE FILL */}
      <directionalLight
        ref={faceLightRef}
        position={facePos}
        intensity={faceIntensity * intensity}
        color={faceColor}
      />

      {/* RIM LIGHT */}
      <spotLight
        ref={rimLightRef}
        position={rimPos}
        angle={0.6}
        penumbra={0.8}
        intensity={rimIntensity * intensity}
        color={rimColor}
        distance={8}
        decay={2}
      />

      {/* AMBIENT */}
      <ambientLight intensity={ambientIntensity * intensity} color={ambientColor} />

      {/* Light position markers (when helpers enabled) */}
      {showHelpers && (
        <>
          {/* Face center - GREEN */}
          <mesh position={fc}>
            <boxGeometry args={[0.3, 0.3, 0.3]} />
            <meshBasicMaterial color="#00ff00" />
          </mesh>
          {/* Key light - YELLOW */}
          <mesh position={keyPos}>
            <boxGeometry args={[0.4, 0.4, 0.4]} />
            <meshBasicMaterial color="#ffff00" />
          </mesh>
          {/* Fill light - CYAN */}
          <mesh position={fillPos}>
            <boxGeometry args={[0.4, 0.4, 0.4]} />
            <meshBasicMaterial color="#00ffff" />
          </mesh>
          {/* Face fill - MAGENTA */}
          <mesh position={facePos}>
            <boxGeometry args={[0.4, 0.4, 0.4]} />
            <meshBasicMaterial color="#ff00ff" />
          </mesh>
          {/* Rim light - ORANGE */}
          <mesh position={rimPos}>
            <boxGeometry args={[0.4, 0.4, 0.4]} />
            <meshBasicMaterial color="#ff8800" />
          </mesh>
        </>
      )}
    </>
  )
}

export default LightingRig
